import styles from "./TeamNameInput.module.scss";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { graphql } from "../../../../__generated__/gql";
import { useMutation } from "urql";
import TextInput from "../../../../ui/TextInput/TextInput";
import Button from "../../../../ui/Button/Button";
import useUser from "../../../../providers/UserProvider/useUser";
import Feedback from "../../../../ui/Feedback/Feedback";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import { useToast } from "../../../../ui/Toast/ToastContext";

const UpdateTeamNameMuation = graphql(/* GraphQL */ `
  mutation updateTeamName($name: String!) {
    updateTenant(input: { name: $name }) {
      id
      name
    }
  }
`);

const schema = z.object({
  name: z.string().min(1, "Name must be at least 1 character long."),
});

type TeamNameInputFormData = z.infer<typeof schema>;

function TeamNameInput() {
  const { tenant } = useUser();
  const { showToast } = useToast();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<TeamNameInputFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: tenant?.name,
    },
  });
  const [, updateTeamName] = useMutation(UpdateTeamNameMuation);

  const onSubmit = async (data: TeamNameInputFormData) => {
    await updateTeamName({
      name: data.name,
    });
    reset({ name: data.name });
    showToast({
      title: "Team name updated",
      variant: "success",
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.container}>
      <UnsavedChangesModal isDirty={isDirty} />
      <div className={styles.inputRow}>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState: { error } }) => (
            <>
              <TextInput {...field} className={styles.input} error={!!error} />
              {error?.message && (
                <Feedback className={styles.feedback}>{error.message}</Feedback>
              )}
            </>
          )}
        />
        <Button
          variant="primary"
          type="submit"
          disabled={isSubmitting || !isDirty}
          loading={isSubmitting}
        >
          Save
        </Button>
      </div>
    </form>
  );
}

export default TeamNameInput;
